import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './QuestionsEditor.module.css';
import QuestionForm from './QuestionForm';
import QuestionRenderer from '../../QuestionRenderer/QuestionRenderer';

export default function QuestionsEditor({
    questions = [],
    previewQuestions = [],
    onChange,
    onAddPage,
    onAddQuestion,
    onUpdateQuestion,
    onRemoveQuestion,
    onRemovePage,
    onMoveWithinPage,
    onInsertPageAt,
    onMovePageToPosition,
    selectedPage: selectedPageProp,
    onSelectedPageChange,
}) {
    const { t } = useTranslation();
    const [answers, setAnswers] = useState({}); // preview answers keyed by questionID
    const grouped = useMemo(() => {
        const map = new Map();
        (questions || []).forEach((q) => {
            const p = Number(q.pageNumber || 1);
            if (!map.has(p)) map.set(p, []);
            map.get(p).push(q);
        });
        return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
    }, [questions]);

    const pageNumbers = grouped.map(([num]) => num);
    const [selectedPage, setSelectedPage] = useState(selectedPageProp ?? (pageNumbers[0] || 1));

    // Keep selected page valid when pages change
    useEffect(() => {
        if (!pageNumbers.length) {
            setSelectedPage(1);
            return;
        }
        const current = selectedPageProp ?? selectedPage;
        if (!pageNumbers.includes(current)) {
            // pick nearest existing page by index
            const idx = pageNumbers.findIndex((n) => n > current);
            const nearest = idx === -1 ? pageNumbers[pageNumbers.length - 1] : pageNumbers[Math.max(0, idx - 1)];
            setSelectedPage(nearest);
            onSelectedPageChange?.(nearest);
        }
    }, [pageNumbers.join(','), selectedPageProp]);

    // Sync internal state when controlled prop changes
    useEffect(() => {
        if (typeof selectedPageProp === 'number' && selectedPageProp !== selectedPage) {
            setSelectedPage(selectedPageProp);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPageProp]);

    // Do not renumber on each edit; keep order as-is and renumber on save/export only
    const emit = (next) => onChange?.(next);

    const addPage = () => {
        if (onAddPage) return onAddPage();
        const maxPage = grouped.length ? grouped[grouped.length - 1][0] : 0;
        emit([...(questions || []), { questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2), pageNumber: maxPage + 1, questionType: 'SC', questionContent: '', details: { options: ['', ''] } }]);
    };

    const insertPageAt = (pos) => {
        const target = Math.max(1, Number(pos) || 1);
        if (onInsertPageAt) return onInsertPageAt(target);
        // Fallback: shift existing pages >= target and insert a default question to realize the new page
        const shifted = (questions || []).map((q) => {
            const pn = Number(q.pageNumber || 1);
            return pn >= target ? { ...q, pageNumber: pn + 1 } : q;
        });
        const newQ = { questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2), pageNumber: target, questionType: 'SC', questionContent: '', details: { options: ['', ''] } };
        emit([...shifted, newQ]);
    };

    const addQuestion = (pageNumber) => {
        if (onAddQuestion) return onAddQuestion(pageNumber);
        emit([...(questions || []), { questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2), pageNumber, questionType: 'SC', questionContent: '', details: { options: ['', ''] } }]);
    };

    const updateQuestion = (qid, patch) => {
        if (onUpdateQuestion) return onUpdateQuestion(qid, patch);
        emit(questions.map((q) => q.questionID === qid ? { ...q, ...patch } : q));
    };

    const removeQuestion = (qid) => {
        if (onRemoveQuestion) return onRemoveQuestion(qid);
        emit(questions.filter((q) => q.questionID !== qid));
    };

    const removePage = (pageNumber) => {
        if (onRemovePage) return onRemovePage(pageNumber);
        emit(questions.filter((q) => Number(q.pageNumber) !== Number(pageNumber)));
    };

    const moveWithinPage = (pageNumber, qid, dir) => {
        if (onMoveWithinPage) return onMoveWithinPage(pageNumber, qid, dir);
        const idxs = [];
        questions.forEach((q, i) => { if (Number(q.pageNumber) === Number(pageNumber)) idxs.push(i); });
        const pos = idxs.findIndex((i) => questions[i].questionID === qid);
        if (pos < 0) return;
        const targetPos = pos + dir;
        if (targetPos < 0 || targetPos >= idxs.length) return;
        const a = idxs[pos];
        const b = idxs[targetPos];
        const next = [...questions];
        const tmp = next[a];
        next[a] = next[b];
        next[b] = tmp;
        emit(next);
    };

    return (
        <div className={styles.wrap}>
            {/* Page navigation and global actions (single page view) */}
            <div className={styles.toolbar}>
                <button
                    type="button"
                    className={styles.btn}
                    onClick={() => {
                        const p = selectedPageProp ?? selectedPage;
                        const next = (pageNumbers[pageNumbers.indexOf(p) - 1] ?? p);
                        setSelectedPage(next);
                        onSelectedPageChange?.(next);
                    }}
                    disabled={!pageNumbers.length || pageNumbers.indexOf(selectedPageProp ?? selectedPage) <= 0}
                >{t('editor.prevPage', { defaultValue: 'Poprzednia strona' })}</button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{t('editor.page', { defaultValue: 'Strona' })}</span>
                    <select className={styles.select} style={{ width: 120 }} value={selectedPageProp ?? selectedPage} onChange={(e) => { const v = Number(e.target.value); setSelectedPage(v); onSelectedPageChange?.(v); }} disabled={!pageNumbers.length}>
                        {pageNumbers.map((n) => (
                            <option key={n} value={n}>{t('editor.pageTitle', { num: n, defaultValue: `Strona ${n}` })}</option>
                        ))}
                    </select>
                </div>

                <button
                    type="button"
                    className={styles.btn}
                    onClick={() => {
                        const p = selectedPageProp ?? selectedPage;
                        const next = (pageNumbers[pageNumbers.indexOf(p) + 1] ?? p);
                        setSelectedPage(next);
                        onSelectedPageChange?.(next);
                    }}
                    disabled={!pageNumbers.length || pageNumbers.indexOf(selectedPageProp ?? selectedPage) >= pageNumbers.length - 1}
                >{t('editor.nextPage', { defaultValue: 'Następna strona' })}</button>

                <div style={{ flex: 1 }} />

                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => addPage()}>+ {t('editor.addPage', { defaultValue: 'Dodaj stronę na końcu' })}</button>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { const cur = selectedPageProp ?? selectedPage; const pos = Math.max(1, Number(cur) || 1); insertPageAt(pos); setSelectedPage(pos); onSelectedPageChange?.(pos); }}>{t('editor.insertPageBefore', { defaultValue: 'Wstaw przed' })}</button>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { const cur = selectedPageProp ?? selectedPage; const pos = Math.max(1, Number(cur) + 1 || 1); insertPageAt(pos); setSelectedPage(pos); onSelectedPageChange?.(pos); }}>{t('editor.insertPageAfter', { defaultValue: 'Wstaw po' })}</button>
            </div>

            {/* Single page card */}
            {pageNumbers.length === 0 ? (
                <div className={styles.pageCard}>
                    <div className={styles.pageHeader}>
                        <div className={styles.pageTitle}>{t('editor.noPages', { defaultValue: 'Brak stron' })}</div>
                        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { insertPageAt(1); setSelectedPage(1); onSelectedPageChange?.(1); }}>+ {t('editor.createFirstPage', { defaultValue: 'Utwórz pierwszą stronę' })}</button>
                    </div>
                </div>
            ) : (
                (() => {
                    const current = grouped.find(([num]) => num === (selectedPageProp ?? selectedPage));
                    const pageNumber = selectedPageProp ?? selectedPage;
                    const items = current ? current[1] : [];
                    // Build preview items for current page (UI language) by position within page
                    const previewMap = (() => {
                        const map = new Map();
                        // group previewQuestions by page and preserve order
                        const page = Number(pageNumber);
                        const arr = (previewQuestions || []).filter((pq) => Number(pq.pageNumber || 1) === page);
                        arr.forEach((pq, idx) => map.set(idx, pq));
                        return map;
                    })();

                    return (
                        <div className={styles.pageCard}>
                            <div className={styles.pageHeader}>
                                <div className={styles.pageTitle}>{t('editor.pageTitle', { num: pageNumber, defaultValue: 'Page {{num}}' })}</div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => addQuestion(pageNumber)}>+ {t('editor.addQuestion', { defaultValue: 'Dodaj pytanie' })}</button>
                                    <button type="button" className={styles.btn} onClick={() => removePage(pageNumber)}>{t('editor.removePage', { defaultValue: 'Usuń stronę' })}</button>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span>{t('editor.movePageTo', { defaultValue: 'Przenieś na pozycję' })}</span>
                                        <select className={styles.select} style={{ width: 160 }} onChange={(e) => {
                                            const v = Number(e.target.value);
                                            if (v > 0) { onMovePageToPosition?.(pageNumber, v); setSelectedPage(v); onSelectedPageChange?.(v); }
                                            e.target.value = '';
                                        }} defaultValue="">
                                            <option value="" disabled>{t('editor.choosePosition', { defaultValue: 'Wybierz pozycję' })}</option>
                                            {pageNumbers.map((n) => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.questionsList}>
                                {items.map((q, i) => {
                                    const previewQ = previewMap.get(i);
                                    const qid = q.questionID;
                                    const curVal = answers[qid];
                                    const handlePreviewChange = (val) => setAnswers((prev) => ({ ...prev, [qid]: val }));
                                    return (
                                        <div key={q.questionID} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, alignItems: 'start' }}>
                                            <QuestionForm
                                                question={q}
                                                positionInPage={i + 1}
                                                onChange={(next) => updateQuestion(q.questionID, next)}
                                                onRemove={() => removeQuestion(q.questionID)}
                                                onMoveUp={() => moveWithinPage(pageNumber, q.questionID, -1)}
                                                onMoveDown={() => moveWithinPage(pageNumber, q.questionID, 1)}
                                                isFirst={i === 0}
                                                isLast={i === items.length - 1}
                                                pageNumbers={pageNumbers}
                                            />
                                            <div style={{ border: '1px solid var(--color-text-secondary)', borderRadius: 8, padding: 10, background: 'var(--color-background-elements)' }}>
                                                <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--color-text-primary)' }}>{previewQ?.questionContent ?? q.questionContent}</div>
                                                {previewQ ? (
                                                    <QuestionRenderer q={previewQ} value={curVal} onChange={handlePreviewChange} />
                                                ) : (
                                                    <QuestionRenderer q={q} value={curVal} onChange={handlePreviewChange} />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()
            )}
        </div>
    );
}
